// axiosInstance, not the cachios client — the switch must never be served
// from a stale cache copy right after the user flips it
import {axiosInstance as http} from "@/http_client";

/**
 * The user's certifications and their on/off switches.
 *
 * One row per certification the platform knows (WA clock hours = 1, WIAA = 2,
 * WSCA = 3, …), each carrying the user's number, lock state and `selected`
 * (the switch). The student app shows a certification's tools only while its
 * switch is on: the dashboard PD section, the Professional Development
 * account tab, the bundle promos and the salary questions follow clock
 * hours; application rows on enrollments and registrations collapse to an
 * "Available with …" line when their certification is off.
 *
 * Nothing here hides a course or a conference the user owns.
 */
export const CLOCK_HOURS = 1;
export const WIAA = 2;
export const WSCA = 3;

export default {
    state: {
        certifications: [],
        certificationsLoaded: false,
    },
    getters: {
        certifications: state => state.certifications,
        certificationsLoaded: state => state.certificationsLoaded,
        certificationById: state => id => state.certifications.find(c => c.certification_id === id) || null,
        // Unknown ids (no row in `certifications`) count as on so an
        // application for an authority we don't model is never hidden.
        certificationOn: state => id => {
            if (!id) return true;
            const c = state.certifications.find(c => c.certification_id === id);
            return c ? !!c.selected : true;
        },
        // Before the first load, assume on: a flash of the full dashboard is
        // better than a flash of the "turn on" nudge for someone who has it on.
        clockHoursOn: state => !state.certificationsLoaded || !!(state.certifications.find(c => c.certification_id === CLOCK_HOURS) || {}).selected,
        wiaaOn: state => !state.certificationsLoaded || !!(state.certifications.find(c => c.certification_id === WIAA) || {}).selected,
        selectedCertificationIds: state => state.certifications.filter(c => c.selected).map(c => c.certification_id),
    },
    mutations: {
        setCertifications(state, rows) {
            state.certifications = Array.isArray(rows) ? rows : [];
            state.certificationsLoaded = true;
        },
        clearCertifications(state) {
            state.certifications = [];
            state.certificationsLoaded = false;
        },
    },
    actions: {
        getCertifications({commit, rootState}) {
            const userId = rootState.user.currentUser && rootState.user.currentUser.id;
            return http.get('app/users/certifications').then(response => {
                // ignore a response that belongs to a previous user (login relay)
                if (rootState.user.currentUser && rootState.user.currentUser.id !== userId) return;
                commit('setCertifications', response.data);
                return response.data;
            });
        },
        // Save a certification card: the number (unless locked) and/or the
        // switch. Reloads the rows afterward and refreshes the dashboard feed,
        // whose sections follow the switches.
        saveCertification({dispatch}, {certification_id, integration_id, certification_number, selected}) {
            const body = {certification_id, integration_id: integration_id || null, certification_number: certification_number || ''};
            if (selected !== undefined) body.selected = !!selected;
            return http.put('app/users/certifications/' + certification_id, body).then(() => {
                return dispatch('getCertifications');
            }).then(rows => {
                dispatch('getDashboard').catch(() => {});
                return rows;
            });
        },
    },
};
